import ApiService from './api.service'
import type {
  CreateTenderDto,
  UpdateTenderDto,
  TenderResponseDto,
  TenderDocumentResponseDto,
  UpdateDocumentDto,
  PageResponseDto,
  TenderQueryParams,
  TenderAuditLogResponseDto,
  AuditLogQueryParams
} from '@/types/tender'

/**
 * Service for managing tender-related API endpoints
 * Handles all CRUD operations for tenders and tender documents
 */
class TenderService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Tender Error'
      }
    })
  }

  /**
   * Create a new tender
   * @param tenderData - The tender data
   * @returns Promise that resolves with the created tender
   */
  async createTender(tenderData: CreateTenderDto): Promise<TenderResponseDto | null> {
    return this.post('/api/tenders', tenderData)
  }

  /**
   * Update an existing tender
   * @param id - The tender ID
   * @param tenderData - The updated tender data
   * @returns Promise that resolves with the updated tender
   */
  async updateTender(id: number, tenderData: UpdateTenderDto): Promise<TenderResponseDto | null> {
    if (!id) {
      return Promise.reject({ message: 'No tender ID provided' })
    }
    return this.put(`/api/tenders/${id}`, tenderData)
  }

  /**
   * Delete a tender
   * @param id - The tender ID
   * @returns Promise that resolves when deletion is complete
   */
  async deleteTender(id: number): Promise<void | null> {
    if (!id) {
      return Promise.reject({ message: 'No tender ID provided' })
    }
    return this.delete(`/api/tenders/${id}`)
  }

  /**
   * Get a tender by ID
   * @param id - The tender ID
   * @returns Promise that resolves with the tender
   */
  async getTenderById(id: number): Promise<TenderResponseDto | null> {
    if (!id) {
      return Promise.reject({ message: 'No tender ID provided' })
    }
    return this.get(`/api/tenders/${id}`)
  }

  /**
   * Get a tender by reference number
   * @param referenceNumber - The tender reference number
   * @returns Promise that resolves with the tender
   */
  async getTenderByReference(referenceNumber: string): Promise<TenderResponseDto | null> {
    if (!referenceNumber) {
      return Promise.reject({ message: 'No reference number provided' })
    }
    return this.get(`/api/tenders/reference/${referenceNumber}`)
  }

  /**
   * Get all open tenders (paginated)
   * @param params - Query parameters for filtering and pagination
   * @returns Promise that resolves with paginated tenders
   */
  async getOpenTenders(params?: TenderQueryParams): Promise<PageResponseDto<TenderResponseDto> | null> {
    const queryString = this.buildQueryString(params)
    return this.get(`/api/tenders/open${queryString}`)
  }

  /**
   * Get tenders created by the current user (paginated)
   * @param params - Query parameters for filtering and pagination
   * @returns Promise that resolves with paginated tenders
   */
  async getMyTenders(params?: TenderQueryParams): Promise<PageResponseDto<TenderResponseDto> | null> {
    const queryString = this.buildQueryString(params)
    return this.get(`/api/tenders/my${queryString}`)
  }

  /**
   * Close a tender (mark as closed for new applications)
   * @param id - The tender ID
   * @returns Promise that resolves with the updated tender
   */
  async closeTender(id: number): Promise<TenderResponseDto | null> {
    if (!id) {
      return Promise.reject({ message: 'No tender ID provided' })
    }
    return this.post(`/api/tenders/${id}/close`, {})
  }

  /**
   * Upload a document to a tender
   * @param tenderId - The tender ID
   * @param file - The document file
   * @param documentType - The type of document
   * @param description - Optional description
   * @param tags - Optional tags (comma-separated)
   * @param isPublic - Whether the document is public
   * @returns Promise that resolves with the created document
   */
  async uploadDocument(
    tenderId: number,
    file: File,
    documentType: string,
    description?: string,
    tags?: string,
    isPublic: boolean = true
  ): Promise<TenderDocumentResponseDto | null> {
    if (!tenderId || !file) {
      return Promise.reject({ message: 'Tender ID and file are required' })
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('documentType', documentType)
    if (description) formData.append('description', description)
    if (tags) formData.append('tags', tags)
    formData.append('isPublic', String(isPublic))

    return this.post(`/api/tenders/${tenderId}/documents`, formData)
  }

  /**
   * Upload multiple documents to a tender in batch
   * @param tenderId - The tender ID
   * @param files - Array of document files
   * @param documentType - The type of documents (same for all)
   * @param description - Optional description (same for all)
   * @param tags - Optional tags (comma-separated, same for all)
   * @param isRequired - Whether documents are required
   * @returns Promise that resolves with array of created documents
   */
  async uploadDocumentsBatch(
    tenderId: number,
    files: File[],
    documentType: string,
    description?: string,
    tags?: string,
    isRequired: boolean = false
  ): Promise<TenderDocumentResponseDto[] | null> {
    if (!tenderId || !files || files.length === 0) {
      return Promise.reject({ message: 'Tender ID and at least one file are required' })
    }

    const formData = new FormData()
    
    // Append all files
    files.forEach(file => {
      formData.append('files', file)
    })
    
    formData.append('documentType', documentType)
    if (description) formData.append('description', description)
    if (tags) formData.append('tags', tags)
    formData.append('isRequired', String(isRequired))

    return this.post(`/api/tenders/${tenderId}/documents/batch`, formData)
  }

  /**
   * Download a tender document
   * @param documentId - The document ID
   * @returns Promise that resolves with the file blob
   */
  async downloadDocument(documentId: number): Promise<Blob | null> {
    console.log('[SERVICE] downloadDocument called:', { documentId })
    
    if (!documentId) {
      console.error('[SERVICE] No document ID provided')
      return Promise.reject({ message: 'No document ID provided' })
    }
    
    const url = `/api/tenders/documents/${documentId}`
    console.log('[SERVICE] Making GET request to:', url, 'with responseType: blob')
    
    try {
      const result = await this.get(url, {}, {
        responseType: 'blob'
      })
      
      console.log('[SERVICE] Response received:', {
        type: typeof result,
        isBlob: result instanceof Blob,
        size: result?.size,
        blobType: result?.type
      })
      
      return result
    } catch (error) {
      console.error('[SERVICE] Download request failed:', error)
      throw error
    }
  }

  /**
   * Delete a tender document
   * @param documentId - The document ID
   * @returns Promise that resolves when deletion is complete
   */
  async deleteDocument(documentId: number): Promise<void | null> {
    if (!documentId) {
      return Promise.reject({ message: 'No document ID provided' })
    }
    return this.delete(`/api/tenders/documents/${documentId}`)
  }

  /**
   * Update tender document metadata
   * @param documentId - The document ID
   * @param updateData - The updated metadata
   * @returns Promise that resolves with the updated document
   */
  async updateDocument(
    documentId: number,
    updateData: UpdateDocumentDto
  ): Promise<TenderDocumentResponseDto | null> {
    if (!documentId) {
      return Promise.reject({ message: 'No document ID provided' })
    }
    return this.put(`/api/tenders/documents/${documentId}`, updateData)
  }

  /**
   * Get all documents for a tender
   * @param tenderId - The tender ID
   * @returns Promise that resolves with the list of documents
   */
  async getTenderDocuments(tenderId: number): Promise<TenderDocumentResponseDto[] | null> {
    if (!tenderId) {
      return Promise.reject({ message: 'No tender ID provided' })
    }
    return this.get(`/api/tenders/${tenderId}/documents`)
  }

  /**
   * Get documents for a tender by type
   * @param tenderId - The tender ID
   * @param documentType - The document type
   * @returns Promise that resolves with the list of documents
   */
  async getTenderDocumentsByType(
    tenderId: number,
    documentType: string
  ): Promise<TenderDocumentResponseDto[] | null> {
    if (!tenderId || !documentType) {
      return Promise.reject({ message: 'Tender ID and document type are required' })
    }
    return this.get(`/api/tenders/${tenderId}/documents/type/${documentType}`)
  }

  /**
   * Import tender from TUNEPS platform by AO number
   * @param aoNumber - The AO number to search for
   * @returns Promise that resolves with the imported tender
   */
  async importFromTuneps(aoNumber: string): Promise<TenderResponseDto | null> {
    if (!aoNumber || !aoNumber.trim()) {
      return Promise.reject({ message: 'AO number is required' })
    }
    
    const params = new URLSearchParams()
    params.append('aoNumber', aoNumber.trim())
    
    return this.post(`/api/tenders/import/tuneps?${params.toString()}`, {})
  }

  /**
   * Build query string from parameters
   * @param params - Query parameters
   * @returns Query string with leading ?
   */
  private buildQueryString(params?: TenderQueryParams): string {
    if (!params) return ''
    
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    
    const queryString = searchParams.toString()
    return queryString ? `?${queryString}` : ''
  }

  // ==================== AUDIT LOG METHODS ====================

  /**
   * Get audit logs for a tender
   * @param tenderId - The tender ID
   * @param params - Query parameters for pagination and filtering
   * @returns Promise that resolves with paginated audit logs
   */
  async getTenderAuditLogs(
    tenderId: number,
    params?: AuditLogQueryParams
  ): Promise<PageResponseDto<TenderAuditLogResponseDto> | null> {
    const queryString = this.buildAuditLogQueryString(params)
    return this.get(`/api/tenders/${tenderId}/audit-logs${queryString}`)
  }

  /**
   * Build query string for audit log requests
   * @param params - The query parameters
   * @returns Query string
   */
  private buildAuditLogQueryString(params?: AuditLogQueryParams): string {
    if (!params) return ''

    const queryParams: Record<string, string> = {}

    if (params.page !== undefined) queryParams.page = params.page.toString()
    if (params.size !== undefined) queryParams.size = params.size.toString()
    if (params.sort) queryParams.sort = params.sort
    if (params.direction) queryParams.direction = params.direction
    if (params.action) queryParams.action = params.action
    if (params.userId !== undefined) queryParams.userId = params.userId.toString()
    if (params.startDate) queryParams.startDate = params.startDate
    if (params.endDate) queryParams.endDate = params.endDate

    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')

    return queryString ? `?${queryString}` : ''
  }
}

// Create a singleton instance
const tenderService = new TenderService()

// Export the singleton
export default tenderService
